import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Download, Mail } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import Reveal from "@/components/Reveal";
import CountUp from "@/components/CountUp";
import SectionHeading from "@/components/SectionHeading";
import ProjectCard from "@/components/ProjectCard";
import { company } from "@/data/site";
import { useT, useContent, useProjects } from "@/i18n";

const clients = [
  "Mercedes-Benz", "Tofaş", "Coca-Cola", "Acıbadem", "Dedeman", "Carrefoursa",
  "Havelsan", "Enerjisa", "Kordsa", "Shell", "Tekfen", "İhlas Holding",
  "TOKİ", "Teknosa", "İpekyol", "Tansaş",
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.13, delayChildren: 0.2 } },
};
const rise = {
  hidden: { y: "118%" },
  show: { y: "0%", transition: { duration: 0.95, ease: [0.22, 1, 0.36, 1] } },
};

export default function Home() {
  const t = useT();
  const { stats, services, certificates, vision } = useContent();
  const { projects, featuredProjects } = useProjects();

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);
  const heroFade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const teaser = projects.slice(0, 8);
  const big = featuredProjects[0];
  const rest = featuredProjects.slice(1, 4);

  const heroLines = t("home.heroLines");
  const hl = t("home.heroHighlight");
  const serviceHighlights = t("home.serviceHighlights");
  const renderHeroLine = (line) => {
    if (typeof line !== "string" || !line.includes(hl)) return line;
    const [pre, post] = line.split(hl);
    return (
      <>
        {pre}
        <span className="text-brand-electric">{hl}</span>
        {post}
      </>
    );
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-end overflow-hidden bg-navy-900" data-testid="hero">
        <motion.div style={{ y: imgY }} className="absolute inset-0 will-change-transform">
          <img src="/sections/hero-stadium.jpg" alt="KYT Yapı Teknolojileri" className="h-[120%] w-full object-cover kenburns" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/65 to-navy-900/30" />
        </motion.div>

        {/* top scrim — keeps the transparent header's white logo & menu legible */}
        <div className="absolute top-0 inset-x-0 h-36 bg-gradient-to-b from-navy-900/75 to-transparent pointer-events-none z-[1]" />

        <div className="absolute top-1/2 -translate-y-1/2 right-[-2.4rem] hidden xl:block rotate-90 origin-center">
          <span className="font-mono text-[11px] tracking-[0.4em] uppercase text-white/35">Est. {company.since} — İzmit / Kocaeli</span>
        </div>

        <motion.div style={{ y: textY, opacity: heroFade }} className="container-kyt relative z-10 pb-24 md:pb-28 pt-40 w-full">
          <motion.div variants={container} initial="hidden" animate="show">
            <div className="overflow-hidden mb-7">
              <motion.div variants={rise}>
                <span className="eyebrow !text-brand-electric">{t("home.heroEyebrow")}</span>
              </motion.div>
            </div>
            <h1 className="font-display text-white font-medium tracking-tight leading-[0.95] text-[clamp(2.4rem,6vw,4.7rem)] max-w-[16ch]">
              {heroLines.map((line, i) => (
                <span key={i} className="block overflow-hidden">
                  <motion.span variants={rise} className="block">{renderHeroLine(line)}</motion.span>
                </span>
              ))}
            </h1>
            <div className="overflow-hidden mt-9">
              <motion.p variants={rise} className="text-white/65 text-lg max-w-xl leading-relaxed font-light">
                {t("home.heroPara", { since: company.since })}
              </motion.p>
            </div>
            <div className="overflow-hidden mt-11">
              <motion.div variants={rise} className="flex flex-wrap items-center gap-8">
                <Link to="/projeler" data-testid="hero-projects-btn" className="group inline-flex items-center gap-3 text-white text-[15px] font-medium tracking-wide">
                  <span className="border-b border-white/40 pb-1.5 group-hover:border-brand-electric transition-colors">{t("common.exploreProjects")}</span>
                  <ArrowRight className="h-4.5 w-4.5 group-hover:translate-x-1.5 transition-transform" />
                </Link>
                <a href={`mailto:${company.email}?subject=${encodeURIComponent(t("common.getQuote"))}`} data-testid="hero-quote-btn" className="inline-flex items-center gap-2.5 text-white/70 hover:text-white text-[15px] tracking-wide transition-colors">
                  <Mail className="h-5 w-5" /> {t("common.getQuote")}
                </a>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>

        {/* scroll cue */}
        <div className="absolute bottom-7 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2.5 z-10">
          <span className="font-mono text-[9.5px] tracking-[0.32em] uppercase text-white/40">{t("home.scroll")}</span>
          <span className="relative h-9 w-px bg-white/15 overflow-hidden">
            <span className="absolute inset-x-0 top-0 h-3 bg-brand-electric scrollcue-dot" />
          </span>
        </div>
      </section>

      {/* CLIENTS — static, calm */}
      <section className="py-16 bg-paper border-b border-ink/10">
        <div className="container-kyt">
          <Reveal>
            <p className="text-center font-mono text-[11px] tracking-[0.22em] uppercase text-slate-400 mb-10">
              {t("home.trustedBy")}
            </p>
          </Reveal>
          <Reveal delay={100}>
            <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
              {clients.map((c) => (
                <span key={c} className="font-display text-lg sm:text-xl text-slate-400 hover:text-brand-blue transition-colors duration-300">
                  {c}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-white border-b border-ink/10">
        <div className="container-kyt grid grid-cols-2 lg:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 80} className={`py-14 lg:py-20 text-center ${i !== 0 ? "lg:border-l border-ink/10" : ""} ${i % 2 === 1 ? "border-l border-ink/10 lg:border-l" : ""} ${i >= 2 ? "border-t border-ink/10 lg:border-t-0" : ""}`}>
              <div className="font-display text-[3.4rem] lg:text-[4.2rem] font-medium text-ink leading-none flex items-baseline justify-center gap-0.5">
                {s.static ? <span>{s.value}</span> : <CountUp end={s.value} />}
                <span className="text-brand-blue text-2xl">{s.suffix}</span>
              </div>
              <div className="mt-4 font-mono text-[10.5px] tracking-[0.16em] uppercase text-slate-400">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* KURUMSAL */}
      <section className="py-28 md:py-36 bg-paper">
        <div className="container-kyt grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <Reveal className="relative">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src="/sections/hero-sanayi.jpg" alt="KYT" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <div>
            <SectionHeading index="01" eyebrow={t("home.sec01Eyebrow")} title={t("home.sec01Title")} desc={vision.text} />
            <Reveal delay={160}>
              <ul className="mt-9 space-y-px">
                {t("home.sec01Bullets").map((tx) => (
                  <li key={tx} className="flex items-start gap-4 py-4 border-t border-ink/10 text-[15.5px] text-slate-700">
                    <span className="text-brand-blue font-mono text-sm mt-0.5">—</span> {tx}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={220}>
              <Link to="/kurumsal" data-testid="home-about-link" className="mt-9 inline-flex items-center gap-2.5 text-ink font-medium group">
                <span className="border-b border-ink/30 pb-1 group-hover:border-brand-blue transition-colors">{t("home.aboutMore")}</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HİZMETLER — numbered, icon-free */}
      <section className="py-28 md:py-36 bg-white">
        <div className="container-kyt">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <SectionHeading index="02" eyebrow={t("home.sec02Eyebrow")} title={services.title} desc={services.intro} />
            <Reveal delay={120}>
              <Link to="/hizmetler" data-testid="home-services-link" className="inline-flex items-center gap-2 text-ink font-medium hover:gap-3 transition-all shrink-0">
                {t("common.allServices")} <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-10">
            {serviceHighlights.map((s, i) => (
              <Reveal key={s.title} delay={i * 80}>
                <div className="group pt-7 border-t border-ink/15">
                  <span className="font-mono text-[12px] text-slate-400 group-hover:text-brand-blue transition-colors">0{i + 1}</span>
                  <h3 className="font-display text-xl font-semibold mt-5 text-ink group-hover:translate-x-1 transition-transform duration-300">{s.title}</h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-slate-500">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ÖNE ÇIKAN PROJELER — gallery bento */}
      <section className="py-28 md:py-36 bg-paper">
        <div className="container-kyt">
          <SectionHeading index="03" eyebrow={t("home.sec03Eyebrow")} title={t("home.sec03Title")} desc={t("home.sec03Desc")} />
          <div className="mt-16 space-y-6">
            <Reveal>
              <Link to={`/projeler/${big.slug}`} data-testid={`featured-${big.slug}`} className="group block">
                <div className="relative overflow-hidden aspect-[16/10] md:aspect-[21/8] bg-navy">
                  <img src={big.cover} alt={big.name} className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.05]" />
                  <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-navy-900/85 via-navy-900/20 to-transparent" />
                  <span className="absolute top-7 right-7 inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 text-white text-[12px] tracking-wide opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-400">
                    {t("common.viewProject")} <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                  <div className="absolute bottom-0 left-0 right-0 p-8 md:p-14 md:max-w-2xl">
                    <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/70">{big.tarih} · {big.location}</span>
                    <h3 className="font-display text-2xl sm:text-4xl font-bold text-white mt-3 leading-tight">{big.name}</h3>
                  </div>
                </div>
              </Link>
            </Reveal>
            <div className="grid sm:grid-cols-3 gap-6">
              {rest.map((p, i) => (
                <Reveal key={p.slug} delay={i * 90}>
                  <Link to={`/projeler/${p.slug}`} data-testid={`featured-${p.slug}`} className="group block">
                    <div className="relative overflow-hidden aspect-[4/5] bg-navy">
                      <img src={p.cover} alt={p.name} className="h-full w-full object-cover transition-transform duration-[1000ms] group-hover:scale-[1.07]" />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-transparent to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <span className="font-mono text-[10px] tracking-wide uppercase text-white/60">{p.tarih}</span>
                        <h3 className="font-display text-lg font-semibold text-white mt-1 leading-tight line-clamp-2">{p.name}</h3>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROJELER SEÇKİ — gallery wall */}
      <section className="py-28 md:py-36 bg-white">
        <div className="container-kyt">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <SectionHeading index="04" eyebrow={t("home.sec04Eyebrow")} title={t("home.sec04Title")} desc={t("home.sec04Desc")} />
            <Reveal delay={120}>
              <Link to="/projeler" data-testid="home-allprojects-link" className="inline-flex items-center gap-2 text-ink font-medium hover:gap-3 transition-all shrink-0">
                {t("common.allProjects")} <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {teaser.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 4) * 70}>
                <ProjectCard project={p} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* KALİTE BELGELERİ — typographic */}
      <section className="py-28 md:py-36 bg-paper">
        <div className="container-kyt">
          <SectionHeading center eyebrow={t("home.certEyebrow")} title={t("home.certTitle")} desc={t("home.certDesc")} />
          <div className="mt-16 grid md:grid-cols-3 gap-10">
            {certificates.map((c, i) => (
              <Reveal key={c.code} delay={i * 100}>
                <div className="border-t border-ink/15 pt-8">
                  <div className="font-display text-2xl font-bold text-ink">{c.code}</div>
                  <div className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-brand-blue mt-2.5">{c.name}</div>
                  <p className="text-[13.5px] text-slate-500 mt-4 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 blueprint opacity-[0.15]" />
        <div className="container-kyt relative py-24 md:py-28 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
          <div className="max-w-2xl">
            <Reveal>
              <div className="eyebrow !text-brand-electric mb-6">{t("home.ctaEyebrow")}</div>
              <h2 className="font-display text-3xl sm:text-[2.9rem] font-bold text-white leading-[1.05]">
                {t("home.ctaTitle")}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={120} className="flex flex-col sm:flex-row gap-8 shrink-0">
            <a href={`mailto:${company.email}?subject=${encodeURIComponent(t("common.getQuote"))}`} data-testid="cta-email" className="group inline-flex items-center gap-3 text-white text-[15px] font-medium">
              <Mail className="h-5 w-5 text-brand-electric" />
              <span className="border-b border-white/40 pb-1.5 group-hover:border-brand-electric transition-colors">{t("common.emailSend")}</span>
            </a>
            <a href={company.catalog} download data-testid="cta-catalog" className="group inline-flex items-center gap-3 text-white text-[15px] font-medium">
              <Download className="h-5 w-5 text-brand-electric" />
              <span className="border-b border-white/40 pb-1.5 group-hover:border-brand-electric transition-colors">{t("common.catalogDownload")}</span>
            </a>
            <Link to="/iletisim" data-testid="cta-contact" className="group inline-flex items-center gap-3 text-white text-[15px] font-medium">
              <span className="border-b border-white/40 pb-1.5 group-hover:border-brand-electric transition-colors">{t("common.contactCta")}</span>
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
